import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { GNB_HEIGHT, HEADER_HEIGHT } from '@/styles';
import CategoryChips from '@/feat-components/ShopSearch/CategoryChips';
import NoticeBanner from '@/feat-components/ShopSearch/NoticeBanner';
import BoardPagination from '@/ui/BoardPagination';
import Card from './kakaoMap/Card';
import KakaoMap from './kakaoMap/KakaoMap';
import ErrorMessage from '@/ui/ErrorMessage';
import { ShippingAddress, SiDo } from '@remodeling/shop';
import { MapSearchFilter } from '@/types';
import { SHOP_COMBINE_FILTER, SHOP_REMODELING_FILTER } from '@/constants';
import { mapType } from '../ShopSearch';
import { CustomScroll } from '@/styles/common';
import { getAsset } from '@/utils';
import TrackingService from '@/services/tracking';
import useMaps from '../hooks/useMaps';
import Image from '@/ui/Image';
import {
  Alert,
  HStack,
  Icongraphy,
  Text,
  VStack,
  Dropdown,
  NewBasicButton,
  Box,
} from '@design-system/pc';

interface MapsProps {
  type?: mapType;
  sidoList?: SiDo[];
  address?: ShippingAddress;
  st: string;
}

const Maps = ({ type = 'entire', sidoList, address, st }: MapsProps) => {
  const {
    center,
    handleChangeDebounceCenter,
    isShowReSearchButton,
    markerListApiData,
    level,
    mapRef,
    isLoading,
    hoverItem,
    shopListNumber,
    sido,
    setSido,
    pages,
    gungu,
    setGungu,
    convertSidoDropdownOptionFormat,
    convertGunGuDropdownOptionFormat,
    clusterListApiData,
    groupCode,
    itemTypes,
    handleClickItemTypes,
    handleClickItemTypesReset,
    handleChangegroupCode,
    setHoverItem,
    setLevel,
    handleClickReSearchButton,
    handleChangeSido,
    handleChangeGunGu,
    handleClickCurrentLoactionButton,
    handleClickCluster,
    handleChangePages,
    openState,
    handleClickMapSearchButton,
    handleToggleMenu,
  } = useMaps({
    sidoList,
    address,
    st,
  });

  const [isNoticeClosed, setIsNoticeClosed] = useState(false);
  const [isNoticePopupOpen, setIsNoticePopupOpen] = useState(false);
  const [noticeBannerHeight, setNoticeBannerHeight] = useState(0);
  const noticeBannerRef = useRef<HTMLElement | null>(null);

  // 리모델링(RM) / 부엌(KC) / 욕실(BR) 중 하나라도 선택되면 소비자원 권고 안내 노출
  const isNoticeVisible =
    !isNoticeClosed &&
    itemTypes.some(
      ({ code }) => code === 'RM' || code === 'KC' || code === 'BR',
    );

  // 배너 본문 줄 수(폰트 폴백 등)에 따라 높이가 달라지므로 상수로 두지 않는다.
  // 실제 높이를 재서 결과 영역 높이 계산에 반영한다.
  useEffect(() => {
    const element = noticeBannerRef.current;

    if (!isNoticeVisible || !element) {
      setNoticeBannerHeight(0);
      return;
    }

    setNoticeBannerHeight(element.offsetHeight);

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      setNoticeBannerHeight(element.offsetHeight);
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [isNoticeVisible]);

  // 안내를 닫았더라도 대상 필터를 다시 선택하는 이벤트마다 반복 노출한다 (기획 확정)
  const handleClickItemTypesWithNotice = useCallback(
    (item: MapSearchFilter) => {
      const isSelectingTarget =
        (item.code === 'RM' || item.code === 'KC' || item.code === 'BR') &&
        !itemTypes.some(({ code }) => code === item.code);

      if (isSelectingTarget) {
        setIsNoticeClosed(false);
      }

      handleClickItemTypes(item);
    },
    [handleClickItemTypes, itemTypes],
  );

  return (
    <Container
      height={
        type === 'remodeling'
          ? `calc(100vh - ${GNB_HEIGHT}px - ${HEADER_HEIGHT}px)`
          : `calc(100vh - ${HEADER_HEIGHT}px)`
      }
      {...TrackingService.tagGTM('shop_search')}
    >
      <MenuContainer width={openState ? '454px' : '0px'}>
        <MenuBox
          display={openState ? 'flex' : 'none'}
          backgroundColor="color_chip.white"
        >
          <VStack
            alignment="start"
            space={20}
            width="100%"
            pt="32px"
            pb="20px"
            pr="28px"
            pl="28px"
          >
            <Text variant="subTitle2">매장찾기</Text>
            <HStack space={8}>
              <SidoDropdownWrapper>
                <Dropdown
                  variant="list"
                  size="md"
                  width="100%"
                  minWidth="160px"
                  placeholder="시, 도"
                  handleChange={handleChangeSido}
                  selectValue={sido?.code}
                  option={{
                    data: convertSidoDropdownOptionFormat?.map((sido) => {
                      return {
                        ...sido,
                        ...TrackingService.tagGTM(
                          'shop_search_sido_dropdown_button',
                          {
                            'option-value': sido?.value,
                          },
                        ),
                      };
                    }),
                    styledProps: {
                      width: '195px',
                      height: '168px',
                    },
                  }}
                ></Dropdown>
              </SidoDropdownWrapper>
              <Dropdown
                variant="list"
                size="md"
                width="100%"
                minWidth="160px"
                placeholder="군,구"
                // sido.code 36인 세종특별자치시만 군,구가 없음
                disabled={!sido || sido?.code === '36'}
                handleChange={handleChangeGunGu}
                selectValue={gungu?.code}
                option={{
                  data: convertGunGuDropdownOptionFormat?.map((gungu) => {
                    return {
                      ...gungu,
                      ...TrackingService.tagGTM(
                        'shop_search_gungu_dropdown_button',
                        {
                          'option-value': gungu?.value,
                        },
                      ),
                    };
                  }),
                  styledProps: {
                    width: '195px',
                    height: '168px',
                  },
                }}
              ></Dropdown>
              <NewBasicButton
                emphasis="low"
                size="md"
                colorVariant="blue"
                onClick={handleClickMapSearchButton}
                disabled={!sido}
              >
                검색
              </NewBasicButton>
            </HStack>
            <FilterContainer>
              {type === 'remodeling' ? (
                <CategoryChips<MapSearchFilter, MapSearchFilter>
                  currentValue={groupCode}
                  type="multi"
                  gap="12px 6px"
                  list={SHOP_REMODELING_FILTER}
                  handleReset={handleClickItemTypesReset}
                  onChange={handleChangegroupCode}
                />
              ) : (
                <CategoryChips<MapSearchFilter, MapSearchFilter>
                  currentValue={itemTypes}
                  type="multi"
                  gap="12px 6px"
                  list={SHOP_COMBINE_FILTER}
                  useCheckIcon
                  hasRefresh={true}
                  handleReset={handleClickItemTypesReset}
                  onChange={handleClickItemTypesWithNotice}
                />
              )}
            </FilterContainer>
          </VStack>
          {isNoticeVisible && (
            <Box
              ref={noticeBannerRef}
              width="100%"
              pl="24px"
              pr="24px"
              flexShrink={0}
            >
              <NoticeBanner
                onClickMore={() => setIsNoticePopupOpen(true)}
                onClickClose={() => setIsNoticeClosed(true)}
              />
            </Box>
          )}
          <ContentsBox
            height={
              type === 'remodeling'
                ? `calc(100vh - ${GNB_HEIGHT}px - ${HEADER_HEIGHT}px - ${
                    240 + noticeBannerHeight
                  }px)`
                : `calc(100vh - ${HEADER_HEIGHT}px - ${
                    288 + noticeBannerHeight
                  }px)`
            }
            type={type}
          >
            {!isLoading ? (
              <ResultList flex="1" minHeight="0">
                {shopListNumber.totalElements ? (
                  <>
                    <HStack space={2} ml="28px" pt="20px">
                      <Text variant="body1">전체</Text>
                      <Box>
                        <Text variant="headline6">
                          {shopListNumber.totalElements || 0}
                        </Text>
                      </Box>
                      <Text variant="body1">개</Text>
                    </HStack>
                    {markerListApiData.map((item, index) => (
                      <Card
                        key={item.id}
                        type={type}
                        item={item}
                        hoverItem={hoverItem}
                        setHoverItem={setHoverItem}
                        isFirstItem={index === 0}
                        {...TrackingService.tagGTM(
                          `shop_search_shop_detail_card_button`,
                          {
                            'shop-id': item.id,
                          },
                        )}
                      />
                    ))}
                  </>
                ) : (
                  <ErrorMessageBox>
                    <ErrorMessage
                      title="이 지역 내에 검색 결과가 없습니다."
                      subTitle={`지도의 위치를 변경하시거나,\n일부 필터를 변경하여 다시 검색해주세요.`}
                    />
                  </ErrorMessageBox>
                )}
              </ResultList>
            ) : (
              <LoadingBox>
                <Image
                  src={getAsset('/assets/icons/loading-obj-new.gif')}
                  height={'40px'}
                  width="40px"
                  alt="loadingIcon"
                />
              </LoadingBox>
            )}
            {shopListNumber.totalElements ? (
              <PaginationBox
                flexShrink={0}
                borderTop="1px solid"
                borderColor="color_chip.gray.50"
              >
                <BoardPagination
                  gtmName="shop_search"
                  page={pages}
                  handlePage={handleChangePages}
                  totalPages={Number(shopListNumber.totalPages) || 1}
                />
              </PaginationBox>
            ) : null}
          </ContentsBox>
        </MenuBox>
        <OnOpenArrow
          backgroundColor="color_chip.white"
          boxShadow="0px 2px 4px rgba(0, 0, 0, 0.25)"
          onClick={handleToggleMenu}
          {...TrackingService.tagGTM(
            `shop_search_${openState ? 'close' : 'open'}_menubox_button`,
          )}
        >
          <IconBox state={openState}>
            <Icongraphy variant="ic_arrow_left_bold" />
          </IconBox>
        </OnOpenArrow>
      </MenuContainer>
      <KakaoMap
        center={center}
        clusterListApiData={clusterListApiData}
        handleChangeCenter={handleChangeDebounceCenter}
        isShowReSearchButton={isShowReSearchButton}
        handleClickReSearchButton={() => {
          handleClickReSearchButton();

          setSido(undefined);
          setGungu(undefined);
        }}
        level={level}
        markerListApiData={markerListApiData}
        setLevel={setLevel}
        hoverItem={hoverItem}
        handleClickCluster={handleClickCluster}
        handleClickCurrentLoactionButton={handleClickCurrentLoactionButton}
        setHoverItem={setHoverItem}
        ref={mapRef}
      />
      <Alert
        isOpen={isNoticePopupOpen}
        title={'리모델링(인테리어) 계약\n유의사항 안내'}
        // DS Text 는 string children 을 dangerouslySetInnerHTML 로 렌더하므로 <u> 가 적용된다.
        // 시안(5649:6249) characterStyleOverrides[0:9] 의 부분 UNDERLINE 재현. 정적 문자열이라 XSS 무관.
        content={
          '<u>한샘이 아닌 상품</u>으로 1,500만원 이상 계약 및 시공 하시는 경우, 전문건설업 면허를 \n보유하고 있는 업체 인지 꼭! 확인 해 주세요.'
        }
        subContent={
          '한샘 정품 계약은 금액과 관계 없이 전문건설업 \n면허를 보유한 ㈜한샘서비스가 직접 시공 합니다.'
        }
        confirmText="확인"
        isShowCloseButton
        // DS Alert 은 title 에 textAlign 을 지정하지 않아 좌측 정렬된다.
        // 시안(5649:6249)은 title 만 center 이므로 모달 컨텐츠에 textAlign 을 걸어 상속시킨다.
        // content/subContent 는 DS 가 textAlign="start" 를 명시하므로 영향받지 않는다.
        contentsStyledProps={{ textAlign: 'center' }}
        handleConfirm={() => setIsNoticePopupOpen(false)}
        handleCloseButton={() => setIsNoticePopupOpen(false)}
        handleCloseDimmed={() => setIsNoticePopupOpen(false)}
      />
    </Container>
  );
};

export default Maps;

// 시·도 Dropdown 을 열었을 때 옵션 텍스트가 길 경우(브라우저별 폭 차이로 2줄이 될 수 있음)
// 1줄 + 말줄임(...)으로 처리하는 안전망. DS(@design-system/pc) DropdownList 옵션 텍스트
// 요소(.dropdown_item_label = Text/TextBox)를 타깃한다.
// 주의: TextBox 가 min-width:auto 를 강제(flex 아이템 min-content)해 말줄임을 무력화하므로,
//       컨테이너 폭에 맞춰 축소되려면 min-width:0 오버라이드가 필수.
//       DS 내부 클래스 의존이므로 @design-system/pc 버전업 시 회귀 확인 필요.
const SidoDropdownWrapper = styled(Box)`
  width: 100%;

  & .dropdown_item_label {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Container = styled(Box)`
  width: 100%;
  position: sticky;
  display: flex;
  top: 0px;
  bottom: 0px;
  left: 0px;
`;

const MenuContainer = styled(Box)`
  display: flex;
  align-items: center;
  position: relative;
  /* transition: 0.4s; */
  height: 100%;
  z-index: 9999;
`;

const ErrorMessageBox = styled(Box)`
  width: 100%;
  margin-top: 80px;
  padding: 24px 28px;
  display: flex;
  justify-content: center;
`;

const MenuBox = styled(Box)`
  width: 454px;
  height: 100%;
  animation-fill-mode: forwards;
  flex-direction: column;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
  z-index: 1;
`;

const ContentsBox = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const OnOpenArrow = styled(Box)`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 80px;
  right: -32px;
`;

const IconBox = styled(Box)<{ state: boolean }>`
  transition-delay: 0.4s;
  transition: 0.4s;
  cursor: pointer;
  transform: ${({ state }) =>
    state ? `rotate3d(0)` : `rotate3d(0, 1, 0, 180deg)`};
`;

const ResultList = styled(Box)`
  width: 100%;
  overflow-y: auto;
  ${CustomScroll}
`;

const LoadingBox = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const PaginationBox = styled(Box)`
  display: flex;
  width: 100%;
  /* 시안(5649:6044) pagenation_area 454x64, 패널 하단 밀착 */
  min-height: 64px;
  box-sizing: border-box;
  justify-content: center;
  align-items: center;
`;

const FilterContainer = styled(Box)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  gap: 12px 6px;
`;
